/**
 * Modal Component
 * Reusable modal dialog for forms and confirmations
 */

import React, { useEffect } from 'react';
import { useUIStore } from '@flow-studio/store'; // import { uiStore';
import { ModalPortal } from '../Modals/ModalPortal';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  maxWidth?: string;
  footer?: React.ReactNode;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  maxWidth = '600px',
  footer,
}) => {
  const { theme } = useUIStore();


  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }


  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.6)',
    backdropFilter: 'blur(4px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 10000,
    padding: '20px',
  };

  const modalStyle: React.CSSProperties = {
    background: theme === 'dark'
      ? 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)'
      : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
    borderRadius: '12px',
    boxShadow: theme === 'dark'
      ? '0 0 40px rgba(58, 140, 69, 0.3)'
      : '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
    border: theme === 'dark'
      ? '1px solid rgba(58, 140, 69, 0.3)'
      : '1px solid #e5e7eb',
    maxWidth,
    width: '100%',
    maxHeight: '95vh',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };

  const headerStyle: React.CSSProperties = {
    padding: '24px',
    borderBottom: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid #e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontWeight: 700,
    color: theme === 'dark' ? '#f8fafc' : '#111827',
    margin: 0,
  };

  const closeButtonStyle: React.CSSProperties = {
    background: 'transparent',
    border: 'none',
    color: theme === 'dark' ? '#94a3b8' : '#64748b',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '4px 8px',
    lineHeight: 1,
    transition: 'color 0.2s',
  };

  const contentStyle: React.CSSProperties = {
    padding: '24px',
    overflowY: 'auto',
    flex: 1,
  };

  const footerStyle: React.CSSProperties = {
    padding: '16px 24px',
    borderTop: theme === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.1)'
      : '1px solid #e5e7eb',
    display: 'flex',
    gap: '12px',
    justifyContent: 'flex-end',
    flexShrink: 0,
  };

  return (
    <ModalPortal>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <h2 style={titleStyle}>{title}</h2>
            <button
              style={closeButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#f8fafc' : '#111827';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.color = theme === 'dark' ? '#94a3b8' : '#64748b';
              }}
              title="Close (Esc)"
            >
              ×
            </button>
          </div>
          <div style={contentStyle}>
            {children}
          </div>
          {footer && <div style={footerStyle}>{footer}</div>}
        </div>
      </div>
    </ModalPortal>
  );
};
